import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { Icon, Tooltip } from '@/shared/ui';

import styles from '../../EncounterDetails.module.scss';
import { EncounterTab } from '../../hooks';
import { getDocumentLabel } from '../../lib/documentTab';
import { getScoreBucket } from '../../lib/getScoreBucket';

type EncounterTabButtonProps = {
  canViewEvaluation: boolean;
  evaluationScore: number | undefined;
  isActive: boolean;
  tab: EncounterTab;
  onClick: (tab: EncounterTab) => void;
};

const SCORE_BUCKET_CLASS = {
  low: styles.scoreBucketLow,
  mid: styles.scoreBucketMid,
  high: styles.scoreBucketHigh,
};

export const EncounterTabButton: FC<EncounterTabButtonProps> = (props) => {
  const { canViewEvaluation, evaluationScore, isActive, tab, onClick } = props;

  const isEvaluationRestricted =
    tab.kind === 'document' &&
    tab.document.type === 'evaluation' &&
    !canViewEvaluation;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-component="encounter-tab-button"
      data-id={tab}
      className={cn(styles.tab, isActive && styles.tabActive)}
      onClick={() => onClick(tab)}
    >
      {tab.kind === 'document' && (
        <>
          <span>{getDocumentLabel(tab.document)}</span>
          {tab.document.type === 'summary' && (
            <Tooltip.Primary
              content={
                <FormattedMessage
                  id="encounters.details.tabSummaryHint"
                  defaultMessage="AI-generated brief encounter summary"
                />
              }
            >
              <Icon name="info" size={14} className={styles.tabInfoIcon} />
            </Tooltip.Primary>
          )}
          {isEvaluationRestricted && (
            <Icon name="lock" size={14} className={styles.tabLockIcon} />
          )}
          {tab.document.type === 'evaluation' &&
            typeof evaluationScore === 'number' && (
              <span
                className={cn(
                  styles.scoreBadge,
                  SCORE_BUCKET_CLASS[getScoreBucket(evaluationScore)],
                )}
              >
                {evaluationScore.toFixed(1)}
              </span>
            )}
        </>
      )}
      {tab.kind === 'analytics' && (
        <FormattedMessage
          id="encounters.details.tabAnalytics"
          defaultMessage="Analytics"
        />
      )}
      {tab.kind === 'transcript' && (
        <FormattedMessage
          id="encounters.details.tabTranscript"
          defaultMessage="Transcript"
        />
      )}
    </button>
  );
};
